// Shared "live" presentation helpers for AgriBooks Sports.
//
// Two surfaces show something running against a clock: the front-desk court
// board (/sports/live, SportsCourtManagementPage) and the ball-machine kiosk's
// "machine in use" card (kiosk/KioskLive.jsx, on the court's Ball Machine page,
// the super admin device page and the fleet line). They must read as one
// system, so the urgency colours, the countdown format and the pulse animation
// live here once. K8 P2.5 P2 lifted them out of the court board, where they
// had been page-local.
//
// Design law (sports-page-design): saturated state colours WITH glow, never a
// faint alpha wash; a live thing carries a pulsing gold dot with a halo.

/* ── Colour buckets ─────────────────────────────────────────────── */

// Time-left bucket for anything with a known end. Same thresholds and hexes
// the court board has used since 2026-07: >30 min green, ≤30 amber, ≤10 red.
export function urgency(minsLeft) {
  if (minsLeft <= 10) return { key: 'red',    fg: '#FF2D55', bg: 'rgba(255,45,85,0.14)',  border: 'rgba(255,45,85,0.45)',  glow: 'rgba(255,45,85,0.35)',  label: 'ENDING SOON' };
  if (minsLeft <= 30) return { key: 'yellow', fg: '#FFB300', bg: 'rgba(255,179,0,0.12)',  border: 'rgba(255,179,0,0.45)',  glow: 'rgba(255,179,0,0.25)',  label: 'WRAPPING UP' };
  return                     { key: 'green',  fg: '#22C55E', bg: 'rgba(34,197,94,0.10)',  border: 'rgba(34,197,94,0.35)',  glow: 'rgba(34,197,94,0.25)',  label: 'IN PROGRESS' };
}

// The clock ran out and the machine is still reported running.
export const LIVE_OVERRUN = { key: 'red',  fg: '#FF2D55', bg: 'rgba(255,45,85,0.14)',  border: 'rgba(255,45,85,0.45)',  glow: 'rgba(255,45,85,0.35)',  label: 'PAST ITS END' };
// Open time: running, no end to count down to. The live gold.
export const LIVE_OPEN    = { key: 'gold', fg: '#FFB300', bg: 'rgba(255,179,0,0.12)',  border: 'rgba(255,179,0,0.45)',  glow: 'rgba(255,179,0,0.25)',  label: 'OPEN TIME' };
// The phone stopped reporting: figures frozen at last contact. Grey, no glow —
// an amber card for a phone nobody has heard from would be a lie in colour.
export const LIVE_PAUSED  = { key: 'grey', fg: '#8A8A8A', bg: 'rgba(115,115,115,0.10)', border: 'rgba(115,115,115,0.30)', glow: 'rgba(0,0,0,0)',          label: 'PAUSED' };

/* ── Formatters ─────────────────────────────────────────────────── */

// Countdown: "MM:SS" under an hour, "H:MM:SS" above. Negative → "0:00".
export function fmtCountdown(mins) {
  if (mins <= 0) return '0:00';
  const totalSec = Math.max(0, Math.round(mins * 60));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// A duration the way a person says it: "45 min", "1 hr", "1 hr 30 min",
// "2 hrs 5 min". K8 P2.5 P6: this is what BILLED time is shown as, everywhere.
export function fmtHrMin(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return '—';
  const m = Math.max(0, Math.round(Number(minutes)));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} min`;
  const hs = `${h} ${h === 1 ? 'hr' : 'hrs'}`;
  return r ? `${hs} ${r} min` : hs;
}

// T1 — one finished rental's time, named the way the kiosk names it on its
// receipt: Booked and Played (the 2026-09-11 contract). `booked` / `played`
// are the server's booked_minutes / played_minutes; `mode` is the row's mode.
//   booked == played  → "2 hrs"
//   they differ       → "2 hrs booked · 1 min played" (an early end, or free
//                        bonus time: "2 hrs booked · 2 hrs 30 min played")
//   open time         → "open · 45 min"
// Returns null when either figure is missing — a RUNNING row, or a server
// from before T1 — so a caller keeps what it showed before:
// `fmtBookedPlayed(r.booked_minutes, r.played_minutes, r.mode) ?? legacy`.
// Both are required on purpose: the court summary now sends the contract's
// `booked_minutes` beside `played_minutes` (it used to send the raw booking),
// so requiring both fields is the guard for a server from before T1.
export function fmtBookedPlayed(booked, played, mode) {
  const ok = (v) => v != null && v !== '' && Number.isFinite(Number(v));
  if (!ok(booked) || !ok(played)) return null;
  const b = Math.max(0, Math.round(Number(booked)));
  const p = Math.max(0, Math.round(Number(played)));
  if (String(mode || '').toUpperCase() === 'OPEN') return `open · ${fmtHrMin(p)}`;
  if (b === p) return fmtHrMin(b);
  return `${fmtHrMin(b)} booked · ${fmtHrMin(p)} played`;
}

/* ── Animation ──────────────────────────────────────────────────── */

export const LIVE_KEYFRAMES = `
@keyframes sports-live-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(1.35); }
}
@keyframes sports-live-dot {
  0%, 100% { box-shadow: 0 0 0 0 rgba(230,57,70,0.55); }
  70%      { box-shadow: 0 0 0 10px rgba(230,57,70,0); }
}
@media (prefers-reduced-motion: reduce) {
  .sports-live-animated { animation: none !important; }
}
`;

const KEYFRAMES_ID = 'sports-live-keyframes';

// Inject the keyframes into <head> once per document. Safe to call from any
// component's effect; a no-op after the first call and outside the browser.
export function ensureLiveKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const el = document.createElement('style');
  el.id = KEYFRAMES_ID;
  el.textContent = LIVE_KEYFRAMES;
  document.head.appendChild(el);
}
